import React, { useEffect, useState, useMemo } from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  Trash2,
  Plus,
  Calendar
} from "lucide-react";

import {
  getAllExams,
  createExam,
  deleteExam
} from "../../api/examsschedule/examApi";
import {
  getAllExamsPortal,
  createExamPortal,
  deleteExamPortal
} from "../../api/examsschedule/examPortalApi";
import {
  getAllExamTypes
} from "../../api/examsschedule/examTypeApi";
import {
  getAllExamTypesPortal
} from "../../api/examsschedule/examTypePortalApi";
import {
  getActiveAcademicYear
} from "../../api/academic/academicYearApi";
import {
  getActiveAcademicYearPortal
} from "../../api/academic/academicYearPortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import ExamStatusBadge from "../../components/examsschedule/ExamStatusBadge";
import ExamTypeBadge from "../../components/examsschedule/ExamTypeBadge";
import {
  EXAM_STATUSES,
  isFullProfile
} from "../../utils/examsschedule/examConstants";
import {
  formatDate,
  pickList,
  toId
} from "../../utils/examsschedule/examHelpers";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const resolveYearName = (res) => {
  if (!res) return "";
  const y = res.academicYear || res.data || res;
  if (typeof y === "string") return y.trim();
  return (
    y.yearName ||
    y.name ||
    y.academicYear ||
    y.label ||
    ""
  )
    .toString()
    .trim();
};

const ExamList = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [exams, setExams] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [activeYearName, setActiveYearName] = useState("");
  const [yearLoading, setYearLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [form, setForm] = useState({
    name: "",
    type: "",
    examTypeId: "",
    startDate: "",
    endDate: "",
    instructions: "",
  });

  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setYearLoading(true);
        const [typeRes, yearRes] = await Promise.all([
          isPortal
            ? getAllExamTypesPortal({ isActive: "true" })
            : getAllExamTypes({ isActive: "true" }),
          isPortal ? getActiveAcademicYearPortal() : getActiveAcademicYear(),
        ]);
        if (cancelled) return;

        const types = pickList(typeRes, ["types", "data"]);
        setExamTypes(types);

        const yearName = resolveYearName(yearRes);
        setActiveYearName(yearName);

        const first = types[0];
        setForm((prev) => ({
          ...prev,
          type: prev.type || first?.code || "",
          examTypeId: prev.examTypeId || toId(first) || "",
        }));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          toast.error(
            e.response?.data?.message ||
              "Could not load active academic year. Set one under Academic Years first."
          );
        }
      } finally {
        if (!cancelled) setYearLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]);

  useEffect(() => {
    if (yearLoading) return;
    fetchExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, isPortal, activeYearName, yearLoading]);

  const basePath = isPortal ? "/staff-panel/exam/exams" : "/exam/exams";

  const fetchExams = async () => {
    try {
      setLoading(true);
      const filters = {
        academicYear: activeYearName || undefined,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      };
      const res = isPortal
        ? await getAllExamsPortal(filters)
        : await getAllExams(filters);
      setExams(pickList(res, ["exams", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load exams");
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (code) => {
    const t = examTypes.find((x) => x.code === code);
    setForm({
      ...form,
      type: code,
      examTypeId: toId(t) || "",
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    const name = (form.name || "").trim();
    const type = (form.type || "").trim();
    const academicYear = (activeYearName || "").trim();

    if (!name) {
      toast.error("Exam name is required");
      return;
    }
    if (!type && !form.examTypeId) {
      toast.error("Select an exam type (create types under Exam Types if empty)");
      return;
    }
    if (!academicYear) {
      toast.error(
        "No active academic year. Set the current year under Academic Years, then reload."
      );
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name,
        type,
        examTypeId: form.examTypeId || undefined,
        academicYear,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        instructions: form.instructions || "",
      };
      const res = isPortal
        ? await createExamPortal(payload)
        : await createExam(payload);
      const created = res.exam || res.data || res;
      toast.success("Exam created");
      const first = examTypes[0];
      setForm({
        name: "",
        type: first?.code || type,
        examTypeId: toId(first) || "",
        startDate: "",
        endDate: "",
        instructions: "",
      });
      if (created?._id) navigate(`${basePath}/${created._id}`);
      else fetchExams();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create exam");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      if (isPortal) await deleteExamPortal(id);
      else await deleteExam(id);
      toast.success("Exam deleted");
      setConfirmDeleteId(null);
      fetchExams();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
      setConfirmDeleteId(null);
    }
  };

  const statusOptions = useMemo(
    () => [
      { label: "All statuses", value: "" },
      ...EXAM_STATUSES.map((s) => ({
        label: s.replace(/_/g, " "),
        value: s,
      })),
    ],
    []
  );

  const typeOptions = useMemo(
    () => [
      { label: "All types", value: "" },
      ...examTypes.map((t) => ({
        label: `${t.name} (${t.code})`,
        value: t.code,
      })),
    ],
    [examTypes]
  );

  const formTypeOptions = useMemo(
    () => [
      {
        label: examTypes.length
          ? "Select type *"
          : "No types — add under Exam Types",
        value: "",
        disabled: true,
      },
      ...examTypes.map((t) => ({
        label: `${t.name} — ${t.scheduleProfile}${
          t.defaultFullMarks ? ` (default ${t.defaultFullMarks})` : ""
        }`,
        value: t.code,
      })),
    ],
    [examTypes]
  );

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (exam) => (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(`${basePath}/${toId(exam)}`)}
            className="p-0 font-medium text-primary-900 dark:text-primary-300 hover:underline h-auto"
          >
            {exam.name}
          </Button>
          {isFullProfile(exam) && <Badge variant="accent">Full</Badge>}
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (exam) => <ExamTypeBadge type={exam.type} />,
    },
    {
      key: "academicYear",
      header: "Year",
      render: (exam) => exam.academicYear,
    },
    {
      key: "status",
      header: "Status",
      render: (exam) => <ExamStatusBadge status={exam.status} />,
    },
    {
      key: "dates",
      header: "Dates",
      render: (exam) => (
        <span>
          {formatDate(exam.startDate)}
          {exam.endDate ? ` – ${formatDate(exam.endDate)}` : ""}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (exam) =>
        canManage && exam.status === "DRAFT" ? (
          confirmDeleteId === toId(exam) ? (
            <div className="inline-flex items-center gap-2">
              <Button
                type="button"
                variant="danger"
                size="xs"
                onClick={() => handleDelete(toId(exam))}
              >
                Confirm
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => setConfirmDeleteId(toId(exam))}
              className="text-neutral-400 dark:text-neutral-500 hover:text-danger-500"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )
        ) : null,
    },
  ];

  const tableRows = useMemo(
    () => exams.map((e) => ({ ...e, id: toId(e) })),
    [exams]
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Examinations</h1>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
            Schedule-only portal — marks are handled separately
          </p>
          <p className="text-xs text-primary-900 dark:text-primary-300 font-medium mt-1">
            Academic year:{" "}
            {yearLoading
              ? "Loading…"
              : activeYearName || "Not set — configure under Academic Years"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() =>
              navigate(isPortal ? "/staff-panel/exam/types" : "/exam/types")
            }
          >
            Exam types
          </Button>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() =>
              navigate(isPortal ? "/staff-panel/exam/rooms" : "/exam/rooms")
            }
          >
            Exam rooms
          </Button>
        </div>
      </div>

      {/* Alerts */}
      
      

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
          />
          <Select
            options={typeOptions}
            value={typeFilter}
            onChange={(val) => setTypeFilter(val)}
          />
        </CardContent>
      </Card>

      {/* Create Exam Card */}
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary-900 dark:text-primary-300 shrink-0" />
              Create Exam
            </CardTitle>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              Academic year is taken from the active year (
              <span className="font-medium text-neutral-700 dark:text-neutral-200">
                {activeYearName || "—"}
              </span>
              ). Type is selected from Exam Types master.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <Input
                  placeholder="Exam name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
                <Select
                  options={formTypeOptions}
                  value={form.type}
                  onChange={(val) => handleTypeChange(val)}
                />
                <div>
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                    Academic Year
                  </label>
                  <div className="border border-neutral-200 dark:border-neutral-800 rounded px-3 py-2 text-sm bg-neutral-50 dark:bg-neutral-950 text-neutral-600 dark:text-neutral-300">
                    {yearLoading ? "Loading year…" : activeYearName || "No active year"}
                  </div>
                </div>
                <Input
                  type="date"
                  label="Start Date"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                />
                <Input
                  type="date"
                  label="End Date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
                <Input
                  placeholder="Instructions (optional)"
                  value={form.instructions}
                  onChange={(e) =>
                    setForm({ ...form, instructions: e.target.value })
                  }
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                isLoading={submitting}
                disabled={yearLoading || !activeYearName}
              >
                Create
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Table Card */}
      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading || yearLoading}
            emptyState={
              <EmptyState
                icon={Calendar}
                title="No exams found"
                description={
                  activeYearName
                    ? `No exams scheduled for ${activeYearName}`
                    : "Configure an active year to load exams."
                }
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ExamList;